import { SimpleGrid } from "@mantine/core";
import {
  IconChevronRight,
  IconUsers,
  IconClipboardText,
  IconPackageImport,
  IconTruckReturn,
} from "@tabler/icons-react";
import { useAuth } from "../../auth/AuthContext";
import { RecordCard } from "../../components/ui";
import { NoteText, Page, PageNote } from "../../shell/Page";

export function ServiceHome() {
  const { hasRole } = useAuth();
  // An outdoor technician has the SERVICE module for step one only, so the
  // tiles they cannot use are not shown. Every endpoint enforces this
  // independently, and since the split the SERVICE reads do too - their queue
  // is their own pickups, not the workshop's.
  //
  // Asked as "holds NO workshop role", not "is an outdoor technician":
  // somebody who collects batteries AND diagnoses them holds both, and
  // reading a single role would have taken the workshop tiles away from them.
  const isFieldOnly = !hasRole(
    "INDOOR_SERVICE_TECHNICIAN",
    "SERVICE_MANAGER",
    "STORE_MANAGER",
    "ADMIN",
  );
  // Collecting is now the outdoor technician's job and NOBODY else's - not
  // the Service Manager, not the Store Manager, not even an Admin. So the
  // tile is hidden from them rather than offering a form whose submit is
  // guaranteed to be refused. An Admin who must collect grants themselves the
  // role first; the tile appears when they do.
  const canCollect = hasRole("OUTDOOR_SERVICE_TECHNICIAN");
  /**
   * Who may hand a delivery out.
   *
   * Narrower than the SERVICE module on purpose: an outdoor technician holds
   * SERVICE and has no business assigning anybody's round. The same three
   * roles the route and the server's DELIVERY_ASSIGNERS name.
   */
  const canAssignDeliveries = hasRole("SERVICE_MANAGER", "STORE_MANAGER", "ADMIN");

  return (
    <Page
      title="Service"
      subtitle={
        isFieldOnly
          ? "Collect batteries from customers"
          : canCollect
            ? "Collection, workshop intake, diagnosis and outcome"
            : "Workshop intake, diagnosis and outcome"
      }
    >
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        {canCollect && (
          <RecordCard
            to="/service/collect"
            leading={<IconPackageImport size={21} stroke={1.6} />}
            primary="Collect a battery"
            secondary="Capture the pickup with a photo and location"
            action={<Chevron />}
          />
        )}
        {!isFieldOnly && (
          <RecordCard
            to="/service/queue"
            leading={<IconClipboardText size={21} stroke={1.6} />}
            primary="Service Center List"
            secondary="Everything still moving through service"
            action={<Chevron />}
          />
        )}
        {!isFieldOnly && (
          <RecordCard
            to="/service/delivery"
            leading={<IconTruckReturn size={21} stroke={1.6} />}
            primary="Ready for Delivery"
            secondary="Finished here, ready for a delivery agent to return"
            action={<Chevron />}
          />
        )}
        {/* DELIVERY EXECUTIVE ALLOTMENT - moved here from the Delivery module.

            The business assigns deliveries through the Service Manager, so
            the queue sits with the rest of their work. It carries everything
            unassigned, sale orders included: an order billed with nobody
            named lands in the same pile, and splitting the screen by origin
            would have left those with nobody watching them.

            Gated on the roles that may hand work out, which is narrower than
            the SERVICE module - an outdoor technician holds SERVICE and has
            no business handing out rounds. */}
        {canAssignDeliveries && (
          <RecordCard
            to="/service/assign-deliveries"
            leading={<IconUsers size={21} stroke={1.6} />}
            primary="Delivery Executive Allotment"
            secondary="Everything waiting for an agent, service and sales alike"
            action={<Chevron />}
          />
        )}
        <RecordCard
          to={isFieldOnly ? "/service/mine" : "/service/jobs"}
          leading={<IconClipboardText size={21} stroke={1.6} />}
          primary={isFieldOnly ? "My collections" : "Service Summary"}
          secondary={
            isFieldOnly
              ? "What you have picked up and where it got to"
              : "Finished and in-progress jobs"
          }
          action={<Chevron />}
        />
      </SimpleGrid>

      {!isFieldOnly && (
        // Rewritten for the four outcomes. It described three, and described
        // them in terms of warranty - "a free replacement under warranty is
        // the only outcome that raises an order", "only applies to a battery
        // actually in warranty". Both stopped being true when the business
        // removed the warranty logic and added the part charge: there are two
        // order-raising outcomes now, and neither is gated on warranty.
        //
        // A PageNote rather than a SectionCard. As a card it was three
        // paragraphs of body text under four tiles and comfortably the
        // largest thing on the screen - standing reference outweighing the
        // work. The rules have not changed; their weight on the page has.
        <PageNote title="What the outcome decides">
          <NoteText>
            A diagnosis ends in exactly one of four outcomes, and the outcome
            alone decides whether goods leave the building. Warranty is not
            checked here — the Service Manager settles that with the customer.
          </NoteText>
          <NoteText>
            <strong>OK</strong> and <strong>Rejected</strong> sell the customer
            nothing, so no order is raised at all. The battery goes back to its
            owner and waits for a delivery agent.
          </NoteText>
          <NoteText>
            <strong>FOC</strong> and <strong>ProRata</strong> each put a new
            battery on the van, so each raises an order and deducts stock when
            it is billed. FOC bills nothing; ProRata bills the flat amount the
            Service Manager agreed, plus GST.
          </NoteText>
        </PageNote>
      )}
    </Page>
  );
}

function Chevron() {
  return <IconChevronRight size={18} stroke={1.8} color="var(--mantine-color-gray-5)" />;
}
